import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  useGridApiRef,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
} from '@mui/x-data-grid-premium';
import StyledDataGrid from '../../components/DataGrid/DataGridStyles';
import { CustomGridFooter } from '../../components/DataGrid/CustomGridFooter';
import { useTableSearch } from '../../hooks/useTableSearch';
import { useCellUndoRedo } from '../../components/UndoRedo/useCellUndoRedo';
import UndoRedoButtons from '../../components/UndoRedo/UndoRedoButtons';
import AutosizeColumnsToggle from '../../components/DataGrid/AutosizeColumnsToggle';
import GroupToggle from '../../components/DataGrid/GroupToggle';
import Toolbar from '../../components/Toolbar/Toolbar';
import LayersIcon from '@mui/icons-material/Layers';
import PieChartIcon from '@mui/icons-material/PieChart';
import EmptyState from '../../components/EmptyState/EmptyState';
import { useViewButtons } from '../../components/Views/useViewButtons';
import { useViewManagement } from '../../hooks/useViewManagement/useViewManagement';
import { HoldingsView } from '../../hooks/useViewManagement/types';
import SaveViewDialog from '../../components/Views/SaveViewDialog';
import SavedViewsPanel from '../../components/Views/SavedViewsPanel';
import ActiveViewIndicator from '../../components/Views/ActiveViewIndicator';
import ViewContextMenu from '../../components/Views/ViewContextMenu';
import { useHoldingsData } from './useHoldingsData';
import { useHoldingsColumns } from './useHoldingsColumns';
import { HoldingsDataTable } from '../../types/generatedTypes';
import { PAGE_TYPES } from '../../constants/pageTypes';
import ManageGroupsModal from '../../components/Groups/ManageGroupsModal';
import {
  transformDataForGrouping,
  getTreeDataPath,
  convertGridColumnsToGroupColumns,
  getRowIdAsString,
} from '../../components/Groups/groupUtils';
import { useGroupingColDef } from '../../components/Groups/useManageGroups';
import { createPinnedColumnsHandler } from '../../utils/gridPinnedColumnsHelper';
import {
  createStateChangeHandler,
  useFilteredSelectionModel,
} from '../../components/Groups/gridStateHelper';
import { useGroupCheckboxHandler } from '../../components/Groups/useGroupCheckboxHandler';

// Re-export for backwards compatibility
export type HoldingsSavedView = HoldingsView;

export const HoldingsPage: React.FC = () => {
  const apiRef = useGridApiRef();

  // State to track current display data for selection handler
  const [currentDisplayData, setCurrentDisplayData] = useState<
    HoldingsDataTable[]
  >([]);

  // First get basic holdings data
  const {
    holdingsData,
    loading,
    selectedRows,
    autosizeColumns,
    setAutosizeColumns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    fetchHoldingsData,
    originalProcessRowUpdate,
    handleProcessRowUpdateError,
    handleExport,
    handleRowSelectionChange,
    expandGroups,
    // Illio instrument search
    searchLoadingRows,
    handleIllioSearch,
    getOptionsForRow,
    handleIllioInstrumentSelect,
    // Groups
    groups,
    setGroups,
    isGroupsModalOpen,
    isGroupingEnabled,
    setIsGroupingEnabled,
    availableRowsForGroups,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  } = useHoldingsData({
    apiRef,
    displayData: currentDisplayData,
  });

  const {
    filteredData: searchFilteredData,
    searchTerm,
    handleSearch,
  } = useTableSearch<HoldingsDataTable>(holdingsData);

  // Add view management hook
  const viewManager = useViewManagement({
    pageType: PAGE_TYPES.HOLDINGS,
    apiRef,
    searchTerm,
    selectedRows,
    autosizeColumns,
    displayData: searchFilteredData,
    handleSearch,
    setSelectedRows: rows => {
      const model = { ids: new Set(rows), type: 'include' as const };
      handleRowSelectionChange(model);
    },
    setAutosizeColumns,
    isGroupingEnabled,
    setIsGroupingEnabled,
    groups,
    setGroups,
  });

  // Apply selected rows filtering when active view has selections
  const baseDisplayData =
    viewManager.filteredDisplayData as HoldingsDataTable[];

  // Transform data for grouping
  const displayData = useMemo(() => {
    return transformDataForGrouping(baseDisplayData, groups, isGroupingEnabled);
  }, [baseDisplayData, groups, isGroupingEnabled]);

  // Calculate actual data row count (excluding group headers)
  const actualRowCount = useMemo(() => {
    return displayData.filter(row => !row.isGroup).length;
  }, [displayData]);

  // Update currentDisplayData for selection handler whenever displayData changes
  useEffect(() => {
    setCurrentDisplayData(displayData);
  }, [displayData]);

  // Create filtered rowSelectionModel to preserve selections during search
  const getRowIdCallback = useCallback(
    (row: HoldingsDataTable) => getRowIdAsString(row),
    []
  );
  const rowSelectionModel = useFilteredSelectionModel(
    selectedRows,
    displayData,
    getRowIdCallback
  );

  // Override applyPinnedColumns to ensure protected columns are always first
  const originalApplyPinnedColumns = viewManager.applyPinnedColumns;
  viewManager.applyPinnedColumns = useCallback(
    createPinnedColumnsHandler(originalApplyPinnedColumns, isGroupingEnabled),
    [originalApplyPinnedColumns, isGroupingEnabled]
  );

  // Initialize undo/redo system
  const {
    wrappedProcessRowUpdate: processRowUpdate,
    undo,
    redo,
    canUndo,
    canRedo,
    getUndoActionInfo,
    getRedoActionInfo,
  } = useCellUndoRedo({
    processRowUpdate: originalProcessRowUpdate,
    getData: () => holdingsData,
  });

  // Generate columns using the custom hook
  const { columns } = useHoldingsColumns({
    autosizeColumns,
    isGroupingEnabled,
    searchLoadingRows,
    handleIllioSearch,
    getOptionsForRow,
    handleIllioInstrumentSelect,
  });

  // Get grouping column definition
  const groupingColDef = useGroupingColDef(isGroupingEnabled);

  // Group checkbox click handler
  const handleCellClick = useGroupCheckboxHandler({
    displayData,
    selectedRows,
    expandGroups,
    onSelectionChange: handleRowSelectionChange,
    getRowId: row => row.id.toString(),
  });

  useEffect(() => {
    fetchHoldingsData();
  }, [fetchHoldingsData]);

  // Get view buttons from hook
  const viewButtons = useViewButtons({
    onSaveView: viewManager.openSaveDialog,
    onOpenSavedViews: viewManager.openSavedViewsPanel,
    loading: loading,
    disabled: false,
  });

  // Helper function to track view modifications
  const updateViewModifications = React.useCallback(() => {
    const modifications = viewManager.compareWithAppliedView();
    viewManager.setViewModifications(modifications);
  }, [viewManager]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 2,
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Toolbar
          onSearch={handleSearch}
          placeholder="Search holdings..."
          searchTerm={searchTerm}
          disabled={loading}
          onExport={handleExport}
          buttons={[
            ...viewButtons,
            {
              variant: 'text',
              label: 'Groups',
              color: 'secondary',
              onClick: handleOpenGroupsModal,
              icon: <LayersIcon />,
              disabled: loading,
              tooltip: 'Organize holdings into groups',
            },
          ]}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mt: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UndoRedoButtons
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={undo}
              onRedo={redo}
              undoDescription={getUndoActionInfo()?.description}
              redoDescription={getRedoActionInfo()?.description}
            />
            <AutosizeColumnsToggle
              checked={autosizeColumns}
              onChange={setAutosizeColumns}
            />
            <GroupToggle
              checked={isGroupingEnabled}
              onChange={setIsGroupingEnabled}
              disabled={groups.length === 0}
            />
          </Box>
          {viewManager.activeView && (
            <ActiveViewIndicator
              view={viewManager.activeView}
              modifications={viewManager.viewModifications}
              onClose={viewManager.clearActiveView}
              onUpdateView={viewManager.updateCurrentView}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ height: 'calc(100vh - 330px)', width: '100%' }}>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : actualRowCount === 0 ? (
          <EmptyState
            icon={
              <PieChartIcon sx={{ fontSize: 80, color: 'text.secondary' }} />
            }
            title="No Holdings Data"
            description="Upload data feeds or sync from Illio to view holdings information"
          />
        ) : (
          <StyledDataGrid
            key={isGroupingEnabled ? 'grouped-grid' : 'normal-grid'}
            apiRef={apiRef}
            rows={displayData}
            rowCount={actualRowCount}
            columns={columns}
            getRowId={row => row.id}
            getRowClassName={params =>
              params.row.isGroup ? 'group-row' : 'data-row'
            }
            checkboxSelection
            isRowSelectable={params => !params.row.isGroup}
            disableRowSelectionOnClick
            rowSelectionModel={rowSelectionModel}
            treeData={isGroupingEnabled}
            getTreeDataPath={isGroupingEnabled ? getTreeDataPath : undefined}
            groupingColDef={groupingColDef}
            defaultGroupingExpansionDepth={isGroupingEnabled ? 1 : undefined}
            columnVisibilityModel={columnVisibilityModel}
            initialState={{
              pinnedColumns: {
                left: [
                  GRID_CHECKBOX_SELECTION_COL_DEF.field,
                  ...(isGroupingEnabled
                    ? [GRID_TREE_DATA_GROUPING_FIELD, 'group_name']
                    : []),
                ],
              },
            }}
            slots={{
              footerRowCount: CustomGridFooter,
            }}
            slotProps={{
              footerRowCount: {
                isGroupingEnabled,
                groups,
              },
            }}
            onCellClick={handleCellClick}
            onRowSelectionModelChange={handleRowSelectionChange}
            onSortModelChange={() => {
              updateViewModifications();
            }}
            onFilterModelChange={() => {
              updateViewModifications();
            }}
            onColumnVisibilityModelChange={newModel => {
              setColumnVisibilityModel(newModel);
              updateViewModifications();
            }}
            onPinnedColumnsChange={() => {
              if (viewManager.activeView) {
                requestAnimationFrame(() => {
                  updateViewModifications();
                });
              }
            }}
            onStateChange={createStateChangeHandler(apiRef, isGroupingEnabled)}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleProcessRowUpdateError}
          />
        )}
      </Box>

      <SaveViewDialog
        open={viewManager.isSaveViewDialogOpen}
        onClose={viewManager.closeSaveDialog}
        onSave={viewManager.saveView}
        editingView={viewManager.editingView}
        editMode={viewManager.editMode}
        currentSearchTerm={searchTerm}
        autosizeColumns={autosizeColumns}
        isGroupingEnabled={isGroupingEnabled}
        selectedRows={selectedRows}
        savedViews={viewManager.savedViews}
        columnFilters={viewManager.getColumnFilters()}
        sortModel={viewManager.getSortModel()}
        columnVisibility={viewManager.getColumnVisibility()}
        pinnedColumns={viewManager.getPinnedColumns()}
        columns={columns}
      />

      <SavedViewsPanel
        views={viewManager.savedViews}
        onViewClick={viewManager.handleViewClick}
        onSaveClick={viewManager.openSaveDialog}
        onMenuClick={viewManager.handleMenuClick}
        activeView={viewManager.activeView}
        open={viewManager.isSavedViewsPanelOpen}
        onClose={viewManager.closeSavedViewsPanel}
      />

      <ViewContextMenu
        anchorEl={viewManager.contextMenuAnchor}
        open={Boolean(viewManager.contextMenuAnchor)}
        onClose={viewManager.handleCloseContextMenu}
        onApply={viewManager.handleApplyView}
        onRename={viewManager.handleRenameView}
        onUpdate={viewManager.handleUpdateView}
        onDelete={viewManager.handleDeleteView}
      />

      <ManageGroupsModal
        open={isGroupsModalOpen}
        onClose={handleCloseGroupsModal}
        onSave={handleSaveGroups}
        availableRows={availableRowsForGroups}
        existingGroups={groups}
        title="Manage Groups"
        columns={useMemo(
          () => convertGridColumnsToGroupColumns(columns),
          [columns]
        )}
        hiddenColumns={['multiplier', 'group_name']}
      />
    </Box>
  );
};
